import {
  Field,
  RelationClass as RelationClassType,
  SortOption,
  YesObject
} from '@yes.technology/react-toolkit'
import { useObjectclass } from 'objectclass/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useLocation, useNavigate } from 'react-router-dom'
import { FieldSettings } from 'shared/types'
import { updateDisplayText } from 'shared/utils'
import DataTable from 'site/Renderer/components/DataTables'
import { DataTableConfigProps } from 'site/Renderer/components/DataTables/DataTable'
import useChangedFieldValues from 'site/Renderer/components/DataTables/hooks/useChangedFieldValues'
import useTableLayouts from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import useTableMode from 'site/Renderer/components/DataTables/hooks/useTableMode'
import {
  ExtraSelectionAction,
  SelectAndLinkOptions,
  SelectionAction
} from 'site/Renderer/components/DataTables/types'
import useLocationState from 'site/Renderer/components/FilterFields/hooks/useLocationState'
import { useFetchYesObjects } from 'yesObject/shared'
import getRelationCounterpartField from '../../lib/getRelationCountertpartField/getRelationCounterpartField'

type RelationClassProps = DataTableConfigProps & {
  relationFieldRelation?: RelationClassType
  fieldValues?: YesObject
  field?: Field
  informational?: boolean
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  fieldSettings?: FieldSettings
  onChange: (fieldColumn: string, value: YesObject[string]) => void
  save: () => Promise<void>
  changeAndSave: (
    fieldColumn: string,
    value: YesObject[string]
  ) => Promise<void>
}

const RelationClass = ({
  relationFieldRelation,
  fieldValues,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  field,
  informational,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  fieldSettings,
  onChange,
  save,
  changeAndSave
}: RelationClassProps) => {
  const { t } = useTranslation()

  const objectclassUuid = relationFieldRelation?.uuid_objectclass || ''

  const { objectclass } = useObjectclass({ objectclassUuid })

  const { fields = {} } = objectclass || {}

  const updatedFields = useMemo(
    () => updateDisplayText(fields, fieldSettings),
    [fieldSettings, fields]
  )

  const columns = useMemo(() => Object.keys(updatedFields), [updatedFields])

  const [tableLayout] = useTableLayouts({ layoutClassificationUuid })

  const orderBy = tableLayout.defaultOrderBy.map(
    ([key, value]) => ({ [key]: value }) as SortOption
  )

  const relationCounterpartField = getRelationCounterpartField({
    relationFieldRelation,
    fieldValues
  })

  const [relationCounterpartColumn] = Object.keys(
    relationCounterpartField || {}
  )

  const {
    yesObjects,
    isFetching,
    pagination,
    totalObjects,
    setPage,
    sortOptions,
    setSortOptions,
    fetchYesObjects: refetchObjects
  } = useFetchYesObjects({
    filterBody: {
      query: {
        $and: [
          {
            [`${relationCounterpartColumn}.uuid`]: {
              $in: [`${fieldValues?.uuid}`]
            },
            'situation.des': {
              $gte: '40'
            }
          }
        ]
      },
      objectclass: objectclassUuid
    },
    enabled: !!relationCounterpartColumn && !!objectclassUuid
  })

  const prevOrderBy = useRef({})

  useEffect(() => {
    if (JSON.stringify(prevOrderBy.current) !== JSON.stringify(orderBy)) {
      setSortOptions(orderBy)
      prevOrderBy.current = orderBy
    }
  }, [orderBy, setSortOptions])

  const { targetField, targetComponent, persistentSelection } =
    useLocationState()

  const selectAndLinkOptions: SelectAndLinkOptions = useMemo(
    () => ({
      targetComponent: 'ObjectView',
      targetField: field?.column_json || '',
      selectedOptions:
        targetComponent === 'ObjectView'
          ? (persistentSelection as YesObject[])
          : undefined
    }),
    [field?.column_json, persistentSelection, targetComponent]
  )

  const [unsavedObjects, setUnsavedObjects] = useState<YesObject[]>([])

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const shouldLinkObjects =
      targetComponent === 'ObjectView' &&
      field?.column_json &&
      targetField === field?.column_json

    if (!shouldLinkObjects || !Array.isArray(persistentSelection)) {
      setUnsavedObjects([])
      return
    }

    setUnsavedObjects((unsavedObjects) => {
      const objectsToLink = persistentSelection?.filter((objectToLink) =>
        yesObjects.every(
          (savedObject) => savedObject.uuid !== objectToLink.uuid
        )
      )

      if (!objectsToLink.length && !unsavedObjects.length) return unsavedObjects

      onChange(field.column_json, [...yesObjects, ...objectsToLink])

      return objectsToLink
    })
  }, [
    persistentSelection,
    yesObjects,
    setUnsavedObjects,
    targetComponent,
    targetField,
    field?.column_json,
    onChange,
    navigate,
    location
  ])

  useEffect(() => {
    if (!Array.isArray(persistentSelection)) return

    const wereObjectsLinked = persistentSelection?.every((objectToLink) =>
      yesObjects.some((savedObject) => savedObject.uuid === objectToLink.uuid)
    )

    if (wereObjectsLinked)
      navigate('.' + location.search, {
        state: {
          ...location.state,
          persistentSelection: undefined,
          targetComponent: undefined,
          targetField: undefined
        },
        replace: true
      })
  }, [location, navigate, persistentSelection, yesObjects])

  const tableObjects = useMemo(
    () => [...yesObjects, ...unsavedObjects],
    [yesObjects, unsavedObjects]
  )

  const handleSave = () => {
    save().then(() => refetchObjects())
  }

  const saveSelectionAction: SelectionAction = {
    children: t('data-tables.save'),
    onClick: handleSave,
    impactClass: 'situationChange',
    pulseAnimate: 'default'
  }

  const unlinkObjects = (objects: YesObject[]) => {
    if (!field?.column_json) return

    const updatedObjects = yesObjects.filter(
      (object) => !objects.some((o) => o.uuid === object.uuid)
    )

    changeAndSave(field.column_json, updatedObjects)
  }

  const primarySelectionAction = unsavedObjects.length
    ? saveSelectionAction
    : undefined

  const extraSelectionActions: ExtraSelectionAction[] = [
    {
      children: t('data-tables.unlink'),
      onClick: unlinkObjects,
      impactClass: 'situationChange'
    }
  ]

  return (
    <DataTable
      informational={informational}
      embedded={true}
      idObjectClass={objectclassUuid}
      objects={tableObjects}
      onSort={setSortOptions}
      setCurrentPage={setPage}
      actionColumnsPosition='last'
      title={field?.display_text.des}
      displayLayoutSelection={false}
      hideEditAction
      disableLinkButton
      insertOnly={tableObjects.length === 0}
      {...{
        objectclass,
        columns,
        fields: updatedFields,
        isLoading: isFetching,
        pagination,
        totalObjects,
        sortOptions,
        actionButton,
        hierarchyLevel,
        refetchObjects,
        relationCounterpartField,
        navigationSliderBackgroundColor,
        navigationSliderColor,
        fieldSettings,
        selectAndLinkOptions,
        primarySelectionAction,
        extraSelectionActions,
        tableLayout
      }}
      {...useTableMode()}
      {...useChangedFieldValues()}
    />
  )
}

export default RelationClass
